import React, { ReactElement, FunctionComponent, useMemo } from 'react'
import Grid from '@sport1/react-elements/Grid'
import Touchable from '@sport1/react-elements/Touchable'
import FontelloIcon from '@sport1/react-elements/FontelloIcon'
import PublishedInfo from '../TeaserInfo/PublishedInfo'
import Channel from '../TeaserInfo/Channel'
import { TextColorTypes } from '../Theme/Fonts/FontTypes'
import { TestID } from '../utils/PropTypesDefinitions'
import { TintColor } from '../BaseComponents/Image/ImageInterfaces'
import { TagImageTypes } from '../BaseComponents/Image/Tag'
import CaptionFont from '../Theme/Fonts/Caption'
import ShareIcon from './Share'
import Duration from './Duration'

export type TeaserInfoProps = {
    /** channel name */
    name?: string
    /** published string (e.g. "vor 8 Min.") */
    published?: string
    /** duration of the content (e.g. for Podcasts) */
    duration?: string
    /** channel imageUrl*/
    imageUrl?: string
    /** channel image alt */
    alt?: string
    /** called when shareIcon is pressed */
    onShare?: () => void
    /** called when alert icon is pressed */
    onSubscribe?: () => void
    /** affects the look of the alert icon */
    subscriptionActive?: boolean
    /**
     * color of texts and icons
     * @defaultValue 'keshi-3'
     * */
    color?: TextColorTypes
    /** color of the channel image */
    tintColor?: TintColor
    /**
     * type of the channel image
     * @defaultValue 'DEFAULT'
     * */
    tagImageType?: TagImageTypes
    /** flag to hide the tag image */
    hideTagImage?: boolean
} & TestID

const insertDividers = (arr: ReactElement[], color?: TextColorTypes): ReactElement[] => {
    const result: ReactElement[] = []
    arr.forEach((item, index) => {
        result.push(item)
        if (index < arr.length - 1) {
            result.push(
                <CaptionFont
                    key={`${index}_divider`}
                    color={color}
                    paddingRight={1}
                    paddingLeft={1}
                >
                    •
                </CaptionFont>
            )
        }
    })
    return result
}

const TeaserInfo: FunctionComponent<TeaserInfoProps> = ({
    name,
    imageUrl,
    alt,
    duration,
    color = 'keshi-3',
    tintColor,
    published,
    testID,
    onShare,
    onSubscribe,
    subscriptionActive,
    tagImageType = 'DEFAULT',
    hideTagImage,
}): ReactElement => {
    const content = useMemo((): ReactElement[] => {
        const result: ReactElement[] = []
        if (name) {
            result.push(
                <Channel
                    name={name}
                    imageUrl={imageUrl}
                    alt={alt}
                    captionColor={color}
                    tintColor={tintColor}
                    key={`channel_${name}`}
                    type={tagImageType}
                    hideImage={hideTagImage}
                />
            )
        }
        if (published) {
            result.push(
                <PublishedInfo
                    testID={`${testID}_publishedInfo`}
                    published={published}
                    color={color}
                    key={`published_${published}`}
                />
            )
        }
        if (duration) {
            result.push(
                <Duration
                    testID={`${testID}_Duration`}
                    duration={duration}
                    color={color}
                    key={`duration${duration}`}
                />
            )
        }
        return insertDividers(result, color)
    }, [
        alt,
        color,
        duration,
        hideTagImage,
        imageUrl,
        name,
        published,
        tagImageType,
        testID,
        tintColor,
    ])

    return (
        <Grid noExpand horizontal minHeight={24} testID={testID}>
            <Grid minWidth={0} horizontal centerVertical>
                {content}
            </Grid>
            {onShare ? (
                <Touchable onPress={onShare} testID={`${testID}_shareButton`}>
                    <Grid noExpand centerVertical minWidth={44} minHeight={24} alignRight>
                        <ShareIcon color="keshi-3" />
                    </Grid>
                </Touchable>
            ) : null}
            {onSubscribe ? (
                <Touchable onPress={onSubscribe}>
                    <Grid noExpand centerVertical minWidth={44} minHeight={24} alignRight>
                        {subscriptionActive ? (
                            <FontelloIcon paddingRight={1} name="alert-active" color="ORANGE" />
                        ) : (
                            <FontelloIcon paddingRight={1} name="alert" color={color} />
                        )}
                    </Grid>
                </Touchable>
            ) : null}
        </Grid>
    )
}

export default TeaserInfo
